import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import RowLink from "_shared/_views/tables/row-link";
import TeamRosterTable, {
  TEAM_LEADER_ROLE,
  TEAM_MEMBER_ROLE,
  TEAM_ROSTER_PERSON_COLUMNS,
  teamRosterCell,
  type TeamRosterRow,
} from "_shared/entity-members/_views/team-roster-table";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { removeFromWorkRowActions, type RemoveFromWork } from "work/work-agreements/teams/_views/remove-from-work-row-actions";
import vendorTeamDepartureRows from "work/work-agreements/teams/vendor-team/_views/vendor-team-departure-rows";
import VendorTeamDeparture from "work/work-agreements/teams/vendor-team/vendor-team-departure";
import { paneIsCompact, workPaneNarrowerThan } from "work/_views/work-pane-layout";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(10),
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  paddingBottom: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    columnGap: theme.spacing(3),
  },
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    paddingBottom: 0,
  },
}));
type VendorTeamReadonlyProps = {
  className?: string;
  leader: Individual | null;
  members: Individual[];
  /** Everyone who has left this vendor team, and why each one left (#5663). */
  departures?: readonly VendorTeamDeparture[];
  /** The people behind those departures, resolved by the tab that owns the directory query. */
  departedIndividuals?: readonly Individual[];
  /**
   * The client team leader's **Remove from work** on each member's row, on running work (#6070).
   * Absent everywhere else, which leaves the roster the frozen record described below.
   */
  removeFromWork?: RemoveFromWork;
};

/**
 * A vendor team as it stands once nobody may change it — an archived proposal, a hired work agreement —
 * drawn as the one Name / Email / Company / Role table every team tab now uses (#4955).
 *
 * The only affordance on it is each person's name, which opens their profile. It carries no search
 * field either: this roster's whole claim is that it offers nothing to do, and MRT's search box brings
 * a clear button of its own once something is typed into it. The editable rosters, which already carry
 * controls, do offer one.
 *
 * ### Why a frozen roster may still be pressed
 *
 * The name is a `RowLink`, so on a surface nobody may change it renders a real `<button>` (#5297).
 * The browser suite forbade every button inside an archived roster's rows, so the two contradicted
 * each other, and #5306 settled it in favour of this component. What that assertion was defending
 * arrived in #5021, beside the removal of the per-collection Accept All / Reject All / Undo All:
 * nothing on a frozen roster may CHANGE it. Opening a profile reads a person and writes nothing
 * here, so the Name cell — and the Company cell, once it opens its own profile — is a reader's
 * gesture, not an action, and stays. The spec now says that in the shape it is true in: outside those
 * cells, no cell in a row may be pressable, and a row is the four declared columns with no controls
 * column. Rendering the name as plain text instead would have cost the keyboard access #5297 gave,
 * on the work agreement's Vendor Team tab as well as the archive, since both are drawn from here.
 */
export default function VendorTeamReadOnly(props: Readonly<VendorTeamReadonlyProps>) {
  const { className, leader, members, removeFromWork } = props;
  const departures = props.departures ?? [];
  const departedIndividuals = props.departedIndividuals ?? [];

  const { openDialog } = useDialog();

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  function openUserProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  /**
   * One person's row.
   *
   * `rowId` is the table's identity for the row, not the person's: a hand-built fixture can carry no
   * userId at all, and two such people must still be two rows rather than collapse into one.
   */
  function rosterRow(person: Individual, rowId: string, role: string, removable = false): TeamRosterRow {
    const firstName = person.profile?.firstName ?? "";
    const lastName = person.profile?.lastName ?? "";
    const fullName = `${firstName} ${lastName}`.trim();
    const removal = removable && removeFromWork && person.userId
      ? removeFromWorkRowActions(removeFromWork, person.userId, fullName || "This member")
      : {};
    return {
      ...removal,
      id: rowId,
      searchText: fullName,
      avatar: <IndividualAvatar individual={person} />,
      /**
       * `RowLink`, the way every other name cell in the application says it opens something (#5244).
       *
       * What this replaced was an anchor with no `href`: focusable, but neither Enter nor Space fires
       * a click on one, so a member's profile was reachable by mouse alone. It also asked for
       * `color: "info"` inside a `styled()` object, where that is not a CSS colour at all — both
       * declarations were dropped and the cell fell back to Material's default link blue, a different
       * colour from the Client Team roster beside it. A button, because a profile opens in a dialog
       * with no URL to point at.
       */
      name: (
        <RowLink ariaLabel={fullName} onClick={() => openUserProfileDialog(person.id)}>
          {firstName} {lastName}
        </RowLink>
      ),
      email: teamRosterCell(person.email?.value),
      company: teamRosterCell(person.entityName),
      role: { content: role },
    };
  }

  const rosterRows: TeamRosterRow[] = [
    ...(leader ? [rosterRow(leader, leader.userId?.value ?? "team-leader", TEAM_LEADER_ROLE)] : []),
    ...(members ?? []).map((member: Individual, index: number) =>
      rosterRow(member, member.userId?.value ?? `team-member-${index}`, TEAM_MEMBER_ROLE, true),
    ),
    // Whoever is gone reads after whoever is still here, so the roster still opens on the team.
    ...vendorTeamDepartureRows({ departures, individuals: departedIndividuals }),
  ];

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamRosterTable
          title="Team Roster"
          surface={removeFromWork ? "work-agreement-vendor-team-client-removal" : "archived-vendor-team"}
          columns={TEAM_ROSTER_PERSON_COLUMNS}
          rows={rosterRows}
          emptyMessage="No Team Members"
        />
      </TeamContainer>
    </MainContainer>
  );
}
